import React, { useState, useRef } from 'react';
import { useData } from '../../contexts/DataContext';
import { useGamification } from '../../contexts/GamificationContext';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiPlus, FiAward, FiCheck, FiActivity, FiTrendingUp, FiMail, FiSave, FiX, FiPhone, FiUser, FiEdit, FiTrash2, FiUpload, FiImage } = FiIcons;

const SuperAdminDashboard = () => {
  const { hosts, addHost, updateHost, deleteHost } = useData();
  const { leaderboard } = useGamification();
  const [showAddHost, setShowAddHost] = useState(false);
  const [editingHost, setEditingHost] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'University',
    contactPerson: '',
    phone: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);

  const hostLeaderboard = leaderboard.filter(entry => entry.role === 'host');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingHost) {
      updateHost(editingHost.id, formData);
      alert('Host updated successfully');
    } else {
      addHost(formData);
      alert('Host created successfully');
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      type: 'University',
      contactPerson: '',
      phone: '',
      logo: null
    });
    setLogoPreview(null);
    setShowAddHost(false);
    setEditingHost(null);
  };

  const handleEdit = (host) => {
    setFormData({
      name: host.name,
      email: host.email,
      type: host.type,
      contactPerson: host.contactPerson || '',
      phone: host.phone || '',
      logo: host.logo || null
    });
    setLogoPreview(host.logo);
    setEditingHost(host);
    setShowAddHost(true);
  };

  const handleDelete = (hostId) => {
    if (window.confirm('Are you sure you want to delete this host? This action cannot be undone.')) {
      deleteHost(hostId);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create a fake local URL for preview and storage in this mock environment
      const objectUrl = URL.createObjectURL(file);
      setLogoPreview(objectUrl);
      setFormData({ ...formData, logo: objectUrl });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Super Admin Dashboard</h1>
        <p className="text-gray-600">Manage Hosts (Universities & Organizations) and monitor system activity.</p>
      </motion.div>

      {/* Stats Overview */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <SafeIcon icon={FiUsers} className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Hosts</p>
              <p className="text-2xl font-bold text-gray-900">{hosts.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg">
              <SafeIcon icon={FiAward} className="text-purple-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Top Host</p>
              <p className="text-2xl font-bold text-gray-900">{hostLeaderboard[0]?.name || 'N/A'}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <SafeIcon icon={FiActivity} className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">System Status</p>
              <p className="text-2xl font-bold text-green-600">Active</p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Host Management */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Manage Hosts</h2>
              <button
                onClick={() => {
                  resetForm();
                  setShowAddHost(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2 text-sm"
              >
                <SafeIcon icon={FiPlus} />
                <span>Add Host</span>
              </button>
            </div>

            {showAddHost && (
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium text-gray-900">{editingHost ? 'Edit Host' : 'Add New Host'}</h3>
                  <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="flex items-start space-x-4">
                    {/* Logo Upload Section */}
                    <div className="w-24 flex-shrink-0">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Logo</label>
                      <div 
                        onClick={triggerFileInput}
                        className={`w-24 h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors overflow-hidden ${logoPreview ? 'border-blue-500' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}`}
                      >
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="w-full h-full object-cover" />
                        ) : (
                          <>
                            <SafeIcon icon={FiUpload} className="text-gray-400 text-xl mb-1" />
                            <span className="text-xs text-gray-500">Upload</span>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileChange} 
                        accept="image/*" 
                        className="hidden" 
                      />
                      {logoPreview && (
                        <button 
                          type="button" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setLogoPreview(null);
                            setFormData({...formData, logo: null});
                          }}
                          className="text-xs text-red-600 mt-1 hover:text-red-800 w-full text-center"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="flex-1 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Host Name *</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SafeIcon icon={FiAward} className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. Innovation University"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                          <select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="University">University</option>
                            <option value="Organization">Organization</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <SafeIcon icon={FiMail} className="text-gray-400" />
                            </div>
                            <input
                              type="email"
                              required
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="admin@host.edu"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <SafeIcon icon={FiUser} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          required
                          value={formData.contactPerson}
                          onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                          className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Full Name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <SafeIcon icon={FiPhone} className="text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-2">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm font-medium inline-flex items-center space-x-2"
                    >
                      <SafeIcon icon={FiSave} />
                      <span>{editingHost ? 'Update Host' : 'Create Host'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="divide-y divide-gray-200">
              {hosts.map((host) => (
                <div key={host.id} className="p-6 flex items-center justify-between hover:bg-gray-50 group">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden ${host.logo ? 'bg-white border border-gray-200' : (host.type === 'University' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600')}`}>
                      {host.logo ? (
                        <img src={host.logo} alt={`${host.name} logo`} className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={host.type === 'University' ? FiAward : FiUsers} className="text-xl" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{host.name}</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-500 gap-1 sm:gap-0">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiMail} className="text-xs" />
                          <span>{host.email}</span>
                        </div>
                        <span className="hidden sm:inline mx-2">•</span>
                        <div className="flex items-center space-x-1">
                          <span>{host.type}</span>
                        </div>
                        {host.contactPerson && (
                          <>
                            <span className="hidden sm:inline mx-2">•</span>
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiUser} className="text-xs" />
                              <span>{host.contactPerson}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 capitalize hidden sm:inline-block">
                      {host.status}
                    </span>
                    <div className="flex space-x-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEdit(host)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="Edit Host"
                      >
                        <SafeIcon icon={FiEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(host.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete Host"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Host Leaderboard */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Host Leaderboard</h2>
              <div className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1">
                <SafeIcon icon={FiTrendingUp} />
                <span>Top Performers</span>
              </div>
            </div>
            <div className="space-y-4">
              {hostLeaderboard.map((entry, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center space-x-4">
                    <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full font-bold ${index === 0 ? 'bg-yellow-100 text-yellow-600' : index === 1 ? 'bg-gray-200 text-gray-600' : index === 2 ? 'bg-orange-100 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{entry.name}</p>
                      <p className="text-xs text-gray-500">Level {entry.level}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{entry.points.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">points</p>
                  </div>
                </div>
              ))}
              {hostLeaderboard.length === 0 && (
                <p className="text-center text-gray-500 py-4">No host activity recorded yet.</p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;